import { create } from 'zustand';
import type { ControlResult, EvidenceContext, EvidencePack } from '@/compliance/evidence/types';
import { buildEvidencePack } from '@/compliance/evidence/engine';
import { auditIdentity } from '@/features/identity/identity';
import { auditIntune } from '@/features/intune/intune';
import { auditApplications } from '@/features/applications/applications';
import type { CapabilityId } from '@/auth/scopes';

/** Audit modules the compliance run can include. */
export type AuditModuleId = 'C8' | 'C9' | 'C10';

export const MODULE_RUNNERS: Record<AuditModuleId, () => Promise<ControlResult[]>> = {
  C8: auditIdentity,
  C9: auditIntune,
  C10: auditApplications,
};

export const MODULE_LABELS: Record<AuditModuleId, string> = {
  C8: 'Identity',
  C9: 'Intune / devices',
  C10: 'Applications & consent',
};

type Status = 'idle' | 'running' | 'done' | 'error';

interface ComplianceState {
  status: Status;
  pack: EvidencePack | null;
  error: unknown;
  runningModule: AuditModuleId | null;
  run: (modules: readonly AuditModuleId[], context: EvidenceContext) => Promise<void>;
  reset: () => void;
}

export const useCompliance = create<ComplianceState>((set) => ({
  status: 'idle',
  pack: null,
  error: null,
  runningModule: null,
  async run(modules, context) {
    if (modules.length === 0) return;
    set({ status: 'running', error: null });
    try {
      const all: ControlResult[] = [];
      for (const m of modules) {
        set({ runningModule: m });
        const results = await MODULE_RUNNERS[m]();
        all.push(...results);
      }
      const pack = buildEvidencePack(all, context);
      set({ status: 'done', pack, runningModule: null });
    } catch (e) {
      set({ status: 'error', error: e, runningModule: null });
    }
  },
  reset: () => set({ status: 'idle', pack: null, error: null, runningModule: null }),
}));

/** The audit modules, in display order, as CapabilityIds. */
export const AUDIT_MODULES: readonly { id: AuditModuleId; cap: CapabilityId }[] = [
  { id: 'C8', cap: 'C8' },
  { id: 'C9', cap: 'C9' },
  { id: 'C10', cap: 'C10' },
];
